import type { WhatsNewAction, WhatsNewCta, WhatsNewGetters, WhatsNewItem } from './whats-new-items'

/** A call to action as offered to this person, in the version of the item they see. */
export interface ResolvedWhatsNewCta extends WhatsNewCta {
  variant: 'available' | 'upsell'
}

/** Everything the rules need to know about the person and the moment. */
export interface WhatsNewContext {
  now: Date
  /** When this person got a Fareclock login. Null when unknown. */
  userCreated: string | Date | null | undefined
  canAccessRoute: (_routeName: string) => boolean
  /** The organization's plan features. */
  features: string[]
  isAccountOwner: boolean
  getters: WhatsNewGetters
}

/** A catalog date (`YYYY-MM-DD`) as local midnight -- `new Date('2026-10-02')` would be UTC. */
export function parseCatalogDate (value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) {
    return new Date(NaN)
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

function toDate (value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null
  }
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** The organization lacks a plan feature the item needs, so it gets the upsell version. */
export function isUpsell (item: WhatsNewItem, context: WhatsNewContext): boolean {
  return (item.requiresFeatures ?? []).some(feature => !context.features.includes(feature))
}

/**
 * Listed in the What's New panel: published, and about something this person can use -- or, for
 * an account owner, something they could buy.
 */
export function isListed (item: WhatsNewItem, context: WhatsNewContext): boolean {
  if (context.now < parseCatalogDate(item.publishedAt)) {
    return false
  }
  if (item.requiresRoute && !context.canAccessRoute(item.requiresRoute)) {
    return false
  }
  if (item.requires && !item.requires(context.getters)) {
    return false
  }
  return !isUpsell(item, context) || context.isAccountOwner
}

/**
 * News to this person: listed, still inside its auto-show window, and published after they
 * started using Fareclock. Seen or not is the caller's business.
 *
 * An unknown login date counts as an existing user, as the tour's variant does: telling a
 * newcomer about a change is mildly redundant, while hiding it from a veteran defeats the point.
 */
export function isNews (item: WhatsNewItem, context: WhatsNewContext): boolean {
  if (!isListed(item, context)) {
    return false
  }
  if (context.now >= parseCatalogDate(item.autoShowUntil)) {
    return false
  }

  const created = toDate(context.userCreated)
  const cutoff = parseCatalogDate(item.autoShowToUsersCreatedBefore ?? item.publishedAt)
  return !created || created < cutoff
}

function routeNameOf (route: WhatsNewCta['route']): string | null {
  return route && typeof route === 'object' && 'name' in route && typeof route.name === 'string' ? route.name : null
}

/**
 * The call to action to offer: the first of the item's alternatives that would work for this
 * person, in the version of the item they see. Null when none would.
 */
export function chooseCta (
  item: WhatsNewItem,
  context: WhatsNewContext,
  isActionAvailable: (_action: WhatsNewAction) => boolean
): ResolvedWhatsNewCta | null {
  const upsell = isUpsell(item, context)
  const alternatives = item.cta ? [item.cta].flat() : []

  for (const cta of alternatives) {
    if (cta.action && !isActionAvailable(cta.action)) {
      continue
    }
    if (cta.requires && !cta.requires(context.getters)) {
      continue
    }
    const routeName = routeNameOf(cta.route)
    if (routeName && !context.canAccessRoute(routeName)) {
      continue
    }
    if (!upsell) {
      return { ...cta, variant: 'available' }
    }
    if (cta.upsellLabel) {
      return { ...cta, label: cta.upsellLabel, variant: 'upsell' }
    }
  }
  return null
}

/** Newest first; ties keep catalog order. */
export function newestFirst (items: WhatsNewItem[]): WhatsNewItem[] {
  return [...items].sort((a, b) => parseCatalogDate(b.publishedAt).getTime() - parseCatalogDate(a.publishedAt).getTime())
}
